/**
 * Node-graph canvas. In edit mode nodes can be dragged, connected from their
 * output ports, selected and deleted; in status mode (run detail) it renders
 * each node's live run status. Edges are SVG curves from port to input.
 */
import { useEffect, useRef, useState, type PointerEvent as RPointerEvent } from 'react';
import { isTyping } from '../../lib/keys';
import { useMasterVariants, useSchema } from '../../api/hooks';
import {
  ArrowRightLeft, BadgeCheck, CalendarClock, CircleCheck, ListTodo, CircleDashed, CircleX, Clock, FileText, GitBranch, Hand, Layers, Loader2, Mail, Pencil, Play, Radio, RefreshCw, Repeat, Search, Send, Server, ShieldCheck, SkipForward, Timer, Trash2, Webhook, Zap,
} from 'lucide-react';
import { NODE_TYPES, TRIGGER_LABELS, describeDateRule, type AutomationEdge, type AutomationGraph, type AutomationNode, type MasterChoiceOrAll, type NodeRunStatus } from '@prism/contracts';
import { assignForDeliveryIcon, describePortalStep } from './portal-steps';
import { describeDetectShotsStep, detectShotsIcon } from './detect-shots-step';
// 1.6.0 (docs/dev/mcp-1.6.0-build.md): site services (W) and AI description (A).
import { describeSiteServiceStep, siteServiceIcon } from './site-service-step';
import { aiStepIcons, describeAiStep } from './ai-steps';

export const NODE_W = 220;
const NODE_H = 72;

const ICONS: Record<AutomationNode['type'], React.ReactNode> = {
  trigger: <Zap size={14} />, condition: <GitBranch size={14} />, validate_metadata: <ShieldCheck size={14} />, update_metadata: <Pencil size={14} />,
  inspect_media: <Search size={14} />, generate_derivatives: <Layers size={14} />, transcode: <RefreshCw size={14} />, external_transcode: <Server size={14} />, copy_to_storage: <Send size={14} />,
  export_asset: <Send size={14} />, export_sequence: <Play size={14} />, export_edl: <FileText size={14} />, request_approval: <Hand size={14} />, call_webhook: <Webhook size={14} />, delete: <Trash2 size={14} />,
  delay: <Timer size={14} />, repeat_until: <Repeat size={14} />, map_metadata: <ArrowRightLeft size={14} />,
  live_source: <Radio size={14} />, run_qc: <BadgeCheck size={14} />, wait_until_date: <CalendarClock size={14} />, create_task: <ListTodo size={14} />,
  assign_for_delivery: assignForDeliveryIcon, detect_shots: detectShotsIcon,
  call_site_service: siteServiceIcon, ...aiStepIcons,
};
export const nodeIcon = (t: AutomationNode['type']) => ICONS[t];

const STATUS_ICON: Partial<Record<NodeRunStatus, React.ReactNode>> = {
  succeeded: <CircleCheck size={14} color="var(--ready)" />, failed: <CircleX size={14} color="var(--danger)" />,
  running: <Loader2 size={14} className="spin" color="var(--processing)" />, retrying: <RefreshCw size={14} className="spin" color="var(--warning)" />,
  waiting_approval: <Clock size={14} color="var(--warning)" />, skipped: <SkipForward size={14} color="var(--faint)" />, pending: <CircleDashed size={14} color="var(--faint)" />,
  cancelled: <CircleX size={14} color="var(--faint)" />,
};

const duration = (s: number) => (s % 86400 === 0 ? `${s / 86400} d` : s % 3600 === 0 ? `${s / 3600} h` : s % 60 === 0 ? `${s / 60} min` : `${s} s`);

/** 1.2.0: a master choice in a node's line ("Textless UHD master", "every master"); empty for the primary. */
function choiceText(c: MasterChoiceOrAll | undefined, variant: (id: string | null) => string): string {
  if (!c || c.type === 'primary') return '';
  if (c.type === 'all') return 'every master';
  if (c.type === 'master') return 'a chosen master';
  return `${variant(c.variantId)}${c.format ? ` ${c.format}` : ''} master`;
}

function nodeSummary(n: AutomationNode, label: (fieldId: string) => string, variant: (id: string | null) => string): string {
  // 1.3.1 (Q4, portal-steps.tsx): Assign for delivery and Wait for delivery.
  const portal = describePortalStep(n);
  if (portal !== null) return portal;
  switch (n.type) {
    case 'trigger': return TRIGGER_LABELS[n.trigger.type] + (n.trigger.type === 'metadata_date' ? ` · ${describeDateRule(n.trigger, label(n.trigger.fieldId))}` : n.trigger.type === 'schedule' ? ` · ${n.trigger.cron}` : n.trigger.type === 'manual' && n.trigger.subject && n.trigger.subject !== 'any' ? ` · on ${n.trigger.subject === 'asset' ? 'an asset' : 'a sequence'}` : n.trigger.type === 'master_added' && n.trigger.variantId ? ` · ${variant(n.trigger.variantId)}` : '');
    case 'condition': return n.all.map((c) => (c.op === 'has_master' || c.op === 'no_master'
      ? `${c.op === 'has_master' ? 'has' : 'no'} ${c.master?.variantId ? variant(c.master.variantId) : 'any'}${c.master?.format ? ` ${c.master.format}` : ''} master`
      : `${c.path.split('.').slice(-1)[0]} ${c.op.replace('_', ' ')} ${c.value ?? ''}`)).join(' and ');
    case 'validate_metadata': return `Requires ${n.required.join(', ')}`;
    case 'update_metadata': return Object.keys(n.values).join(', ') || 'No fields yet';
    case 'transcode':
    case 'external_transcode': {
      const what = n.type === 'transcode' ? (/^[0-9a-f-]{36}$/.test(n.preset) ? 'Transcode profile' : n.preset) : n.profileId;
      const from = choiceText(n.source, variant);
      const to = n.saveAs?.type === 'master' ? ` → ${n.saveAs.variantId ? variant(n.saveAs.variantId) : 'same variant'} master` : '';
      return `${from ? `${from} · ` : ''}${what}${to}`;
    }
    case 'request_approval': return n.userId ? 'By one person' : `By ${(n.role ?? '').replace('_', ' ')}`;
    case 'copy_to_storage': return `${n.representation === 'output' ? `Output of ${n.fromNode ?? '?'}` : n.representation === 'original' ? choiceText(n.master, variant) || 'original' : n.representation} → location`;
    case 'delete': return n.target === 'asset' ? 'Asset → recycle bin' : `${n.representation === 'output' ? `Output of ${n.fromNode ?? '?'}` : n.representation === 'original' ? choiceText(n.master, variant) || 'original' : n.representation} from a location${n.onlyCopy === 'fail' ? ' · keeps the last copy' : ''}`;
    case 'export_asset': return `${n.quality === 'original' ? choiceText(n.master, variant) || 'primary master' : 'proxy'} · ${n.preset === 'original' ? 'original format' : n.preset}${n.range === 'programme' ? ' · programme' : ''}`;
    case 'export_sequence': return `${n.sequenceId ? '' : 'Run’s sequence · '}${n.preset} · ${n.quality}`;
    case 'export_edl': return 'CMX 3600 of the run’s sequence';
    case 'delay': return `Wait ${duration(n.seconds)}`;
    case 'repeat_until': return `${n.check.type === 'asset' ? 'Re-read the asset' : n.check.type === 'webhook' ? 'Call an endpoint' : n.check.type === 'site_service' ? describeSiteServiceStep(n.check) : 'Wait'} every ${duration(n.everySeconds)}, up to ${n.maxAttempts}×`;
    case 'map_metadata': return `${n.mappings.length} field${n.mappings.length === 1 ? '' : 's'}${n.mappings.some((m) => m.mode === 'if_empty') ? ' · fills empty fields' : ''}`;
    case 'live_source': return n.action === 'start' ? 'Start listening and recording' : 'Stop (ends a recording cleanly)';
    case 'call_webhook': return n.path ? `…/${n.path}` : NODE_TYPES[n.type].description;
    case 'wait_until_date': return describeDateRule(n, label(n.fieldId));
    case 'create_task': return `For ${n.assignee.type === 'role' ? n.assignee.key.replace(/_/g, ' ') : n.assignee.type === 'group' ? 'a group' : 'one person'}${n.due.type === 'none' ? '' : n.due.type === 'now' ? ` · due in ${n.due.days ? `${n.due.days} d ` : ''}${n.due.hours ? `${n.due.hours} h` : ''}`.trimEnd() : ` · due ${describeDateRule(n.due, label(n.due.fieldId)).replace(/^On /, 'on ')}`}`;
    case 'run_qc': return `${n.source === 'output' ? `Output of ${n.fromNode ?? '?'}` : n.source === 'proxy' ? 'Proxy' : choiceText(n.master, variant) || 'Original'} · ${n.profileId ? 'chosen profile' : 'default profile'}`;
    case 'detect_shots': return describeDetectShotsStep(n);
    case 'call_site_service': return describeSiteServiceStep(n);
    case 'for_each_shot':
    case 'for_each_segment':
    case 'update_segment': return describeAiStep(n);
    default: return NODE_TYPES[n.type].description;
  }
}

const portOffset = (node: AutomationNode, port: AutomationEdge['port']) => {
  const ports = NODE_TYPES[node.type].ports;
  const i = ports.indexOf(port);
  return { x: node.position.x + NODE_W, y: node.position.y + (NODE_H * (i + 1)) / (ports.length + 1) };
};

export function GraphCanvas({ graph, onChange, selected, onSelect, statuses, problems, readOnly = false }: {
  graph: AutomationGraph;
  onChange?: (g: AutomationGraph) => void;
  selected: string | null;
  onSelect: (id: string | null) => void;
  statuses?: Map<string, NodeRunStatus>;
  problems?: Set<string>;
  readOnly?: boolean;
}) {
  const root = useRef<HTMLDivElement>(null);
  const fields = useSchema().data?.definition.fields;
  const fieldLabel = (id: string) => fields?.find((f) => f.id === id)?.label ?? id;
  const variants = useMasterVariants().data?.items;
  const variantName = (id: string | null) => (id ? variants?.find((v) => v.id === id)?.name ?? 'a variant' : 'Programme');
  const [drag, setDrag] = useState<{ id: string; dx: number; dy: number; sx: number; sy: number } | null>(null);
  const [link, setLink] = useState<{ from: string; port: AutomationEdge['port']; x: number; y: number } | null>(null);
  const [selectedEdge, setSelectedEdge] = useState<number | null>(null);

  // Zoom out (down to 70%) so the whole graph fits the available width.
  const [avail, setAvail] = useState(0);
  useEffect(() => {
    const el = root.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setAvail(el.clientWidth));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const extent = Math.max(...graph.nodes.map((n) => n.position.x + NODE_W)) + 60;
  const scale = avail ? Math.max(0.7, Math.min(1, avail / extent)) : 1;

  const local = (e: { clientX: number; clientY: number }) => {
    const r = root.current!.getBoundingClientRect();
    return { x: (e.clientX - r.left + root.current!.scrollLeft) / scale, y: (e.clientY - r.top + root.current!.scrollTop) / scale };
  };
  const positions = new Map(graph.nodes.map((n) => [n.id, drag?.id === n.id ? { x: n.position.x + drag.dx, y: n.position.y + drag.dy } : n.position]));
  const at = (n: AutomationNode) => ({ ...n, position: positions.get(n.id)! });

  const onPointerMove = (e: RPointerEvent) => {
    if (drag) setDrag({ ...drag, dx: (e.clientX - drag.sx) / scale, dy: (e.clientY - drag.sy) / scale });
    if (link) setLink({ ...link, ...local(e) });
  };
  const onPointerUp = (e: RPointerEvent) => {
    if (drag && onChange && (drag.dx || drag.dy)) {
      onChange({ ...graph, nodes: graph.nodes.map((n) => (n.id === drag.id ? { ...n, position: { x: Math.max(0, Math.round((n.position.x + drag.dx) / 10) * 10), y: Math.max(0, Math.round((n.position.y + drag.dy) / 10) * 10) } } : n)) });
    }
    if (link && onChange) {
      const p = local(e);
      const target = graph.nodes.find((n) => p.x >= n.position.x && p.x <= n.position.x + NODE_W && p.y >= n.position.y && p.y <= n.position.y + NODE_H);
      if (target && target.id !== link.from && target.type !== 'trigger' && !graph.edges.some((x) => x.from === link.from && x.port === link.port && x.to === target.id)) {
        onChange({ ...graph, edges: [...graph.edges, { from: link.from, port: link.port, to: target.id }] });
      }
    }
    setDrag(null);
    setLink(null);
  };

  const width = Math.max(1200, ...graph.nodes.map((n) => n.position.x + NODE_W + 200));
  const height = Math.max(600, ...graph.nodes.map((n) => n.position.y + NODE_H + 200));
  const curve = (x1: number, y1: number, x2: number, y2: number) => {
    const c = Math.max(40, Math.abs(x2 - x1) / 2);
    return `M ${x1} ${y1} C ${x1 + c} ${y1}, ${x2 - c} ${y2}, ${x2} ${y2}`;
  };

  return (
    <div className="graph-canvas" ref={root} onPointerMove={onPointerMove} onPointerUp={onPointerUp}
      onPointerDown={(e) => { if (e.target === root.current || (e.target as Element).tagName === 'svg' || (e.target as Element).classList.contains('graph-world')) { onSelect(null); setSelectedEdge(null); } }}
      tabIndex={0} data-testid="graph-canvas"
      onKeyDown={(e) => {
        if (readOnly || !onChange) return;
        if ((e.key === 'Delete' || e.key === 'Backspace') && !isTyping(e.target, 'input, textarea, select')) {
          if (selectedEdge !== null) { onChange({ ...graph, edges: graph.edges.filter((_, i) => i !== selectedEdge) }); setSelectedEdge(null); }
          else if (selected) {
            const n = graph.nodes.find((x) => x.id === selected);
            if (n && n.type !== 'trigger') { onChange({ nodes: graph.nodes.filter((x) => x.id !== selected), edges: graph.edges.filter((x) => x.from !== selected && x.to !== selected), schemaVersion: 1 }); onSelect(null); }
          }
        }
      }}>
      <div className="graph-world" style={{ width: width * scale, height: height * scale }}>
      <div style={{ transform: `scale(${scale})`, transformOrigin: '0 0', width, height, position: 'relative' }}>
      <svg width={width} height={height} className="graph-edges">
        <defs><marker id="arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse"><path d="M 0 0 L 10 5 L 0 10 z" fill="var(--border-strong)" /></marker></defs>
        {graph.edges.map((e, i) => {
          const from = graph.nodes.find((n) => n.id === e.from);
          const to = graph.nodes.find((n) => n.id === e.to);
          if (!from || !to) return null;
          const a = portOffset(at(from), e.port);
          const bPos = positions.get(to.id)!;
          const b = { x: bPos.x, y: bPos.y + NODE_H / 2 };
          const active = statuses && ['succeeded', 'running', 'retrying', 'waiting_approval', 'failed'].includes(statuses.get(to.id) ?? '') && statuses.get(from.id) === 'succeeded';
          return (
            <g key={i} className={`edge ${selectedEdge === i ? 'selected' : ''} ${active ? 'active' : ''}`} onPointerDown={(ev) => { ev.stopPropagation(); if (!readOnly) { setSelectedEdge(i); onSelect(null); } }}>
              <path d={curve(a.x, a.y, b.x, b.y)} className="edge-hit" />
              <path d={curve(a.x, a.y, b.x, b.y)} className="edge-line" markerEnd="url(#arrow)" />
              {e.port !== 'next' && <text x={a.x + 10} y={a.y - 5} className="edge-label">{e.port}</text>}
            </g>
          );
        })}
        {link && (() => {
          const from = graph.nodes.find((n) => n.id === link.from)!;
          const a = portOffset(at(from), link.port);
          return <path d={curve(a.x, a.y, link.x, link.y)} className="edge-line pending" />;
        })()}
      </svg>
      {graph.nodes.map((n) => {
        const pos = positions.get(n.id)!;
        const info = NODE_TYPES[n.type];
        const st = statuses?.get(n.id);
        return (
          <div key={n.id} className={`graph-node cat-${info.category} ${selected === n.id ? 'selected' : ''} ${st ? `st-${st}` : ''} ${problems?.has(n.id) ? 'has-problem' : ''}`}
            style={{ left: pos.x, top: pos.y, width: NODE_W, height: NODE_H }} data-node={n.id}
            onPointerDown={(e) => {
              e.stopPropagation();
              onSelect(n.id);
              setSelectedEdge(null);
              if (!readOnly) { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); setDrag({ id: n.id, dx: 0, dy: 0, sx: e.clientX, sy: e.clientY }); }
            }}>
            {n.type !== 'trigger' && <span className="graph-in" />}
            <div className="graph-node-head">{ICONS[n.type]}<span>{n.label || info.label}</span>{st && <span className="graph-status">{STATUS_ICON[st]}</span>}</div>
            <div className="graph-node-sub">{nodeSummary(n, fieldLabel, variantName)}</div>
            {info.ports.map((port, i) => (
              <span key={port} className={`graph-port port-${port}`} style={{ top: (NODE_H * (i + 1)) / (info.ports.length + 1) - 6 }} title={`Connect “${port}”`}
                onPointerDown={(e) => {
                  e.stopPropagation();
                  if (readOnly) return;
                  (root.current as HTMLElement).setPointerCapture?.(e.pointerId);
                  setLink({ from: n.id, port, ...local(e) });
                }} />
            ))}
          </div>
        );
      })}
      </div>
      </div>
      {!readOnly && graph.nodes.length === 1 && <div className="graph-hint"><Mail size={14} /> Add steps from the palette, then drag from a node’s right-hand port to connect it.</div>}
    </div>
  );
}
